// components/stations/stations.component.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { BsGripVertical, BsFolder, BsFolder2Open, BsFolderPlus, BsInputCursorText, BsTrash3Fill, BsThreeDotsVertical } from "react-icons/bs";
import { db } from "../../firebase/firebase.utils";
import StationsBody from "../collab-projects/StationsBody";
import RetroButton from "../retro/button/retro-button.component";
import StationItem from "../station-item/station-item.component.class";
import CreateStation from "../create-station/create-station.component";
import CreateFolder from "../create-folder/create-folder.component";
import { ContextMenu } from "../context-menu/ContextMenu";
import InvitetoPremium from "../marketing/invite-to-premium.component";
import { folderOf, folderNames, listId, projectId, overlayFolders, saveFolders } from "./station-folders";
import "./stations.styles.scss";

// This wrapper improves only this screen's menus; the shared ContextMenu stays unchanged.
function ProjectContextMenu({ menu, items, onAction, onClose }) {
  const host = useRef(null);
  useLayoutEffect(() => {
    const element = host.current.firstElementChild;
    const rect = element.getBoundingClientRect();
    element.style.left = `${Math.max(8, Math.min(menu.left, window.innerWidth - rect.width - 8))}px`;
    element.style.top = `${Math.max(8, Math.min(menu.top, window.innerHeight - rect.height - 8))}px`;
    element.setAttribute("role", "menu");
    const options = Array.from(element.children);
    options.forEach((option) => {
      option.setAttribute("role", "menuitem");
      option.tabIndex = -1;
    });
    options[0]?.focus();
    const outside = (event) => { if (!host.current?.contains(event.target)) onClose(); };
    const dismiss = () => onClose();
    document.addEventListener("pointerdown", outside);
    window.addEventListener("resize", dismiss);
    // Ignore scrolling within the menu itself.
    const scroll = (event) => { if (!host.current?.contains(event.target)) onClose(); };
    window.addEventListener("scroll", scroll, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("scroll", scroll, true);
    };
  }, [menu, onClose]);
  const keyDown = (event) => {
    const options = Array.from(host.current.firstElementChild.children);
    const index = options.indexOf(document.activeElement);
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
      options[next]?.focus();
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      options[index]?.click();
    } else if (event.key === "Escape" || event.key === "Tab") {
      event.preventDefault();
      onClose();
      menu.trigger?.focus();
    }
  };
  return createPortal(
    <div ref={host} className="stations-menu-host" onKeyDown={keyDown} onContextMenu={(event) => event.preventDefault()}>
      <ContextMenu top={menu.top} left={menu.left} menuItems={items} onMenuItemClick={onAction} />
    </div>, document.body,
  );
}

function ProjectRow({ project, spaceId, activeId, provided, snapshot, onMenu, busy }) {
  const inFolder = folderOf(project) !== null;
  return (
    <div ref={provided.innerRef} {...provided.draggableProps}
      className={`station-project-row${inFolder ? " is-infolder" : ""}${snapshot.isDragging ? " is-dragging" : ""}`}
      onContextMenu={(event) => onMenu(event, "project", project)}>
      <div className={project.stationId === activeId ? "stations__item-active" : "stations__item"}>
        <span className="project__drag" {...provided.dragHandleProps} aria-label={`Move ${project.name || "project"}`}>
          <BsGripVertical />
        </span>
        <StationItem data={project} spaceId={spaceId} />
      </div>
      <button type="button" className="station-project-row__more" disabled={busy || snapshot.isDragging}
        aria-label={`Options for ${project.name || "project"}`} aria-haspopup="menu" onClick={(event) => onMenu(event, "project", project)}>
        <BsThreeDotsVertical size="4em" />
      </button>
    </div>
  );
}

// The same list and project row serve folders and the outside area.
function ProjectList({ folder, projects, spaceId, activeId, onMenu, dragging, busy, canDrag }) {
  const [open, setOpen] = useState(false);
  const isFolder = folder !== null;
  const rowProps = { spaceId, activeId, onMenu, busy };
  return (
    <Droppable droppableId={listId(folder)} isDropDisabled={busy || !canDrag}
      renderClone={(provided, snapshot, rubric) => (
        <ProjectRow {...rowProps} project={projects[rubric.source.index]} provided={provided} snapshot={snapshot} />
      )}>
      {(provided, snapshot) => (
        <section ref={provided.innerRef} {...provided.droppableProps}
          className={`station-project-list${isFolder ? (open ? " is-folder is-open" : " is-folder") : " is-root"}${snapshot.isDraggingOver ? " is-over" : ""}`}>
          {isFolder ? (
            <header className="station-project-list__header" onContextMenu={(event) => onMenu(event, "folder", folder)}>
              <div className={open ? "foldersItemHeader" : "foldersItemHeader-close"}
                onClick={() => { if (!dragging && !busy) setOpen((value) => !value); }}>
                <RetroButton disabled={dragging || busy} aria-expanded={open}>
                  {open ? <BsFolder2Open size="1.2em" /> : <BsFolder size="1.2em" />}
                  <span title={folder}>{folder}</span>
                </RetroButton>
              </div>
              <button type="button" className="station-project-row__more" aria-label={`Options for folder ${folder}`}
                aria-haspopup="menu" disabled={dragging || busy} onClick={(event) => onMenu(event, "folder", folder)}><BsThreeDotsVertical /></button>
            </header>
          ) : <div className="station-project-list__label">Outside folders</div>}
          {(!isFolder || open) && projects.map((project, index) => (
            <Draggable key={projectId(project)} draggableId={projectId(project)} index={index} isDragDisabled={busy || !canDrag}>
              {(rowProvided, rowSnapshot) => (
                <ProjectRow {...rowProps} project={project} provided={rowProvided} snapshot={rowSnapshot} />
              )}
            </Draggable>
          ))}
          {provided.placeholder}
          {!projects.length && (
            <div className="station-project-list__empty">{dragging ? "Drop project here" : "No projects here"}</div>
          )}
        </section>
      )}
    </Droppable>
  );
}

function WorkspaceProjects({ spaceId, activeId, stations, entitlement, currentUser, showCreateControls }) {
  const [changes, setChanges] = useState({});
  const [dragProjects, setDragProjects] = useState(null);
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const mounted = useRef(true);
  const [error, setError] = useState("");
  const [menu, setMenu] = useState(null);
  const [modal, setModal] = useState(null);
  const [invitation, setInvitation] = useState(false);
  const [createStation, setCreateStation] = useState(false);
  const canUseFolders = Boolean(entitlement) && String(entitlement).toLowerCase() !== "free";
  const projects = useMemo(() => overlayFolders(stations, changes), [stations, changes]);
  const visibleProjects = dragProjects || projects;
  const folders = folderNames(visibleProjects);
  const closeMenu = React.useCallback(() => setMenu(null), []);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Keep optimistic moves until the existing Redux listener acknowledges them.
  // Never alter a captured list or clear an optimistic move while a save is pending.
  useEffect(() => {
    if (busy || dragProjects) return;
    setChanges((current) => {
      const remaining = { ...current };
      let changed = false;
      Object.keys(current).forEach((id) => {
        const station = stations.find((item) => item.stationId === id);
        if (!station || folderOf(station) === current[id]) {
          delete remaining[id];
          changed = true;
        }
      });
      return changed ? remaining : current;
    });
  }, [stations, busy, dragProjects]);

  const moveProjects = async (affected, folder) => {
    if (saving.current) throw new Error("Please wait for the current save.");
    if (folder !== null && !canUseFolders) throw new Error("Your plan does not include folders.");
    const moved = affected.filter((project) => folderOf(project) !== folder);
    if (!moved.length) return;
    saving.current = true;
    setBusy(true);
    setError("");
    const previous = changes;
    const next = Object.fromEntries(moved.map((project) => [project.stationId, folder]));
    setChanges((current) => ({ ...current, ...next }));
    try {
      await saveFolders(db, spaceId, moved, folder);
    } catch (err) {
      if (mounted.current) setChanges(previous);
      throw err;
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const openMenu = (event, type, value) => {
    event.preventDefault();
    event.stopPropagation();
    if (saving.current || dragProjects) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const rightClick = event.type === "contextmenu";
    setMenu({ type, value, trigger: event.currentTarget,
      top: rightClick ? event.clientY : rect.bottom,
      left: rightClick ? event.clientX : rect.left });
  };
  const openFolderModal = (options) => {
    if (!canUseFolders) setInvitation(true);
    else setModal(options);
  };
  const act = async (label) => {
    const selected = menu;
    closeMenu();
    try {
      if (label === "Move to Folder") openFolderModal({ mode: "move", project: selected.value });
      if (label === "Remove from Folder") await moveProjects([selected.value], null);
      if (label === "Rename Folder") openFolderModal({ mode: "rename", folder: selected.value });
      if (label === "Delete Folder" && window.confirm(`Delete folder “${selected.value}”? Its projects will move outside folders. No projects will be deleted.`)) {
        await moveProjects(projects.filter((project) => folderOf(project) === selected.value), null);
      }
    } catch (err) { if (mounted.current) setError(err.message || "Could not save changes. Please try again."); }
  };
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    // Apply the optimistic folder move synchronously, before waiting for Firestore.
    if (destination && source.droppableId !== destination.droppableId) {
      const project = visibleProjects.find((item) => projectId(item) === draggableId);
      const folder = destination.droppableId === "root" ? null
        : folders.find((item) => listId(item) === destination.droppableId);
      if (project && folder !== undefined) {
        moveProjects([project], folder).catch((err) => {
          if (mounted.current) setError(err.message || "Could not move the project. Please try again.");
        });
      }
    }
    setDragProjects(null);
  };
  const items = menu?.type === "folder" ? [
    { label: "Rename Folder", icon: <BsInputCursorText /> },
    { label: "Delete Folder", icon: <BsTrash3Fill /> },
  ] : [
    { label: "Move to Folder", icon: <BsFolderPlus /> },
    ...(menu && folderOf(menu.value) ? [{ label: "Remove from Folder", icon: <BsFolder2Open /> }] : []),
  ];
  return <>
    {error && <div className="stations__error" role="alert">{error}<button type="button" onClick={() => setError("")}>Dismiss</button></div>}
    <div className="stations__save-status" role="status">{busy ? "Saving changes…" : ""}</div>
    <DragDropContext onBeforeCapture={() => { closeMenu(); setDragProjects(projects); }} onDragEnd={onDragEnd}>
      <div className="stations__lists">
        {[...folders, null].map((folder) => (
          <ProjectList key={listId(folder)} folder={folder} projects={visibleProjects.filter((project) => folderOf(project) === folder)}
            spaceId={spaceId} activeId={activeId} onMenu={openMenu} dragging={Boolean(dragProjects)} busy={busy} canDrag={canUseFolders} />
        ))}
      </div>
    </DragDropContext>
    {showCreateControls && <div className="stations__actions stations__createStation">
      <RetroButton mode="flat" color="info" disabled={busy || Boolean(dragProjects)} onClick={() => { if (!busy && !dragProjects) openFolderModal({ mode: "create" }); }}>Create new folder</RetroButton>
      <RetroButton mode="flat" color="info" disabled={busy || Boolean(dragProjects)} onClick={() => { if (!busy && !dragProjects) setCreateStation((value) => !value); }}>{createStation ? "Cancel" : "Create new project"}</RetroButton>
    </div>}
    {showCreateControls && createStation && <CreateStation setCreateStation={setCreateStation} activeSpaceId={spaceId} />}
    {menu && <ProjectContextMenu menu={menu} items={items} onAction={act} onClose={closeMenu} />}
    {modal && <CreateFolder {...modal} projects={projects} onSave={moveProjects} onClose={() => setModal(null)} />}
    {invitation && <InvitetoPremium setShowInvitation={setInvitation} featureName="Creating Folders"
      featureDesc="organize your projects and ideas efficiently"
      featureImg="https://images.pexels.com/photos/4792282/pexels-photo-4792282.jpeg" currentUser={currentUser} />}
  </>;
}

export default function Stations() {
  return <StationsBody WorkspaceProjects={WorkspaceProjects} />;
}
